/**
 * Custom-column cell for `date` columns. Wraps the shared `<DatePicker>`
 * compound (Popover + Calendar from monks-ui). The persisted value is an
 * ISO date string ("YYYY-MM-DD"); clearing the value sets it to null.
 */
import { format } from 'date-fns';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { Button, Calendar, Popover, PopoverContent, PopoverTrigger } from '@monksflow/monks-ui';
import { useState } from 'react';

import { parseLocalDate } from '../../lib/parseLocalDate';
import { useTranslation } from 'react-i18next';

import { EM_DASH } from './constants';

interface DateCustomCellProps {
  /** ISO date string (YYYY-MM-DD). */
  value: string | undefined;
  onChange: (next: string | null) => void;
}

function toISODate(date: Date | undefined): string | null {
  if (!date) return null;
  return format(date, 'yyyy-MM-dd');
}

export function DateCustomCell({ value, onChange }: DateCustomCellProps) {
  const { t } = useTranslation('trackers');

  const [open, setOpen] = useState(false);
  const parsed = parseLocalDate(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => e.stopPropagation()}
          className="w-full justify-center px-1 text-sm font-normal"
        >
          <CalendarIcon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
          {parsed ? (
            // eslint-disable-next-line i18next/no-literal-string -- date-fns format pattern, machine input to format() rather than translator-facing copy
            <span>{format(parsed, 'MMM d, yyyy')}</span>
          ) : (
            <span className="text-muted-foreground">{EM_DASH}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0" onClick={(e) => e.stopPropagation()}>
        <Calendar
          mode="single"
          selected={parsed}
          onSelect={(next) => {
            const iso = toISODate(next);
            // null = clear; "" = no change
            if (iso !== value) onChange(iso);
            setOpen(false);
          }}
        />
        {parsed ? (
          <div className="border-t border-border p-1">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-muted-foreground"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
            >
              <X className="size-3.5" aria-hidden />
              {t('trackersBoard.dateCustomCell.clear')}
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
